/**
 * Shared helpers for the TanStack code-based route tree (the react-router v3
 * replacement). Encapsulates the recurring conversion patterns so each feature
 * route module stays declarative:
 *
 *  - `layoutComponent(Layout)` wraps a legacy layout that renders
 *    `this.props.children` so its matched child renders through `<Outlet/>` —
 *    this lets us keep the layout components untouched.
 *  - `redirectRoute(...)` reproduces RR3's `<Redirect>` / `<IndexRedirect>` as a
 *    leaf whose `beforeLoad` throws `redirect()`.
 *  - `guardRedirect(...)` builds a `beforeLoad` for RR3 redirect-only `onEnter`
 *    guards (e.g. `requireTeacherStatus`).
 *
 * RR3 path syntax maps as: `(/:x)` optional param → `{-$x}`; a `*` splat →
 * handled by the root `notFoundComponent`; an optional STATIC prefix group like
 * `(class/:classroomId/)assignments` cannot be one TanStack path — declare two
 * routes instead.
 */
import * as React from 'react';
import {createRoute, notFound, redirect, type AnyRoute} from '@tanstack/react-router';

import sessionStore from 'client/Session/SessionStore';
import {LOG_IN_PAGE_BASE_PATH, storePostLoginRedirect} from 'lib/postLoginRedirect';
import {withRouter} from 'lib/router/withRouter';
import {InjectedOutlet} from 'lib/router/layoutContext';

// Re-exported so feature route modules import everything routing-related from
// one place. (Raw `Outlet` is intentionally NOT re-exported — layouts must use
// `layoutComponent`/`InjectedOutlet` so the RR3 cloneElement prop-injection
// bridge is preserved; a bare `Outlet` would silently drop layout-injected props.)
export {createRoute, notFound, redirect, withRouter};
export type {AnyRoute};

type ParentRouteFn = () => AnyRoute;
// The router is intentionally un-registered (untyped), so `to`/params are loose.
type To = string;
type ParamsArg =
  | Record<string, unknown>
  | ((matched: Record<string, unknown>) => Record<string, unknown>);

/**
 * Wrap a legacy layout component (one that renders `this.props.children`) so its
 * children render via TanStack's `<Outlet/>`, and so it receives the RR3 prop
 * bag (`router`/`location`/`params`/`route`/`routes`) that the router used to
 * inject into route components. This keeps the layout component itself unchanged.
 */
export function layoutComponent<P extends object>(
  Layout: React.ComponentType<P & {children?: React.ReactNode}>
): React.FC {
  const Inner: React.FC = (props) => (
    <Layout {...(props as unknown as P)}>
      <InjectedOutlet />
    </Layout>
  );
  Inner.displayName = `Layout(${Layout.displayName || Layout.name || 'Component'})`;
  return withRouter(Inner) as React.FC;
}

/**
 * Build a redirecting route (RR3 `<Redirect from to>` / `<IndexRedirect to>`).
 * `to` is an absolute TanStack path (e.g. `'/school-admin/$schoolId/dashboard'`);
 * by default the matched params are passed through so `$param` placeholders in
 * `to` resolve.
 */
export function redirectRoute(options: {
  getParentRoute: ParentRouteFn;
  path: string;
  to: To;
  params?: ParamsArg;
}): AnyRoute {
  const {getParentRoute, path, to, params} = options;
  return createRoute({
    getParentRoute,
    path,
    beforeLoad: ({params: matched}: {params: Record<string, unknown>}) => {
      const resolvedParams = typeof params === 'function' ? params(matched) : (params ?? matched);
      throw redirect({to, params: resolvedParams} as never);
    }
  }) as AnyRoute;
}

/**
 * Build a `beforeLoad` that redirects to `to` unless `allow()` returns true —
 * the TanStack form of RR3's redirect-only `onEnter` guards.
 */
export function guardRedirect(allow: () => boolean, to: To) {
  return () => {
    if (!allow()) {
      throw redirect({to} as never);
    }
  };
}

/** `beforeLoad`'s destination location — the router is un-registered, so type it loosely. */
export interface BeforeLoadArgs {
  location: {pathname: string; searchStr?: string};
}

/**
 * `beforeLoad` guard for a route that needs a session: sends a signed-out visitor to the log-in
 * page, remembering the deep link they asked for so `UserRedirectUtil.redirect()` returns them to
 * it once they authenticate.
 *
 * A ROLE guard must run this FIRST: `isTeacher()` and friends are also false when signed out, so
 * bouncing on the role check alone discards the URL. The path comes from `beforeLoad`'s own
 * `location` (the destination) — the live history still holds the page being navigated away from.
 */
export function requireSessionBeforeLoad({location}: BeforeLoadArgs): void {
  if (sessionStore.hasValidSession()) {
    return;
  }
  storePostLoginRedirect(`${location.pathname}${location.searchStr ?? ''}`);
  throw redirect({to: LOG_IN_PAGE_BASE_PATH} as never);
}
